import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as Form from '@radix-ui/react-form';
import { Lock, Mail, Loader2, AlertCircle } from 'lucide-react';
import { useError } from '../../context/ErrorContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { showError } = useError();

  const validateForm = () => {
    const errors = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      const redirectTo = location.state?.from?.pathname || '/aadashmin/dashboard'; 
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      
      // Show appropriate error message based on error type
      if (err.code === 'auth/invalid-credentials') {
        showError('Invalid email or password. Please check your credentials and try again.', 'error', 'AUTH001', {
          email,
          timestamp: new Date(),
          attempts: (parseInt(localStorage.getItem('loginAttempts') || '0') + 1)
        });
        localStorage.setItem('loginAttempts', (parseInt(localStorage.getItem('loginAttempts') || '0') + 1).toString());
      } else if (err.code === 'auth/too-many-requests') {
        showError('Too many failed attempts. Please try again later.', 'error', 'AUTH002');
      } else if (err.code === 'auth/network-error') {
        showError('Network error. Please check your connection and try again.', 'error', 'AUTH003');
      } else {
        showError('An unexpected error occurred. Please try again.', 'error', 'AUTH999', {
          errorMessage: err.message
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-accent rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome Back
          </h2>
          <p className="text-gray-600">
            Sign in to access your admin dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white shadow-xl rounded-lg p-8">
          <Form.Root className="space-y-6" onSubmit={handleSubmit}>
            <Form.Field className="space-y-2" name="email">
              <Form.Label className="block text-sm font-medium text-gray-700">
                Email
              </Form.Label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <Form.Control asChild>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setValidationErrors((prev) => ({ ...prev, email: '' }));
                    }}
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      validationErrors.email ? 'border-accent/10' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent sm:text-sm`}
                    placeholder="Enter your email" 
                    required
                  />
                </Form.Control>
                {validationErrors.email && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <AlertCircle className="h-5 w-5 text-accent" />
                  </div>
                )}
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-sm text-accent">{validationErrors.email}</p>
              )}
            </Form.Field>

            <Form.Field className="space-y-2" name="password">
              <Form.Label className="block text-sm font-medium text-gray-700">
                Password
              </Form.Label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Form.Control asChild>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setValidationErrors((prev) => ({ ...prev, password: '' }));
                    }}
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      validationErrors.password ? 'border-accent/' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent sm:text-sm`}
                    placeholder="••••••••"
                    required
                  />
                </Form.Control>
                {validationErrors.password && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {validationErrors.password && (
                <p className="mt-1 text-sm text-accent">{validationErrors.password}</p>
              )}
            </Form.Field>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent ${
                  isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </Form.Root>

        </div>
      </div>
    </div>
  );
};

export default Login;